import React, { useState, useEffect } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import CaptchaVerification from "./CaptchaVerification";
import AuthCard from "./auth/AuthCard";
import Notice from "./ui/Notice";
import { Button, Field, Input } from "../designSystem";
import { validateEmail, translateAuthError } from "../utils";
import { getFullApiUrl, podcastId } from "../config";

export default function ForgotPassword() {
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState(location.state?.email || "");
  const [validEmail, setValidEmail] = useState(true);
  const [message, setMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [captchaConfig, setCaptchaConfig] = useState(null);
  const [showCaptcha, setShowCaptcha] = useState(false);

  // Fetch CAPTCHA config on mount
  useEffect(() => {
    fetchCaptchaConfig();
  }, []);

  const fetchCaptchaConfig = async () => {
    try {
      const response = await axios.get(
        getFullApiUrl("/api/v1/auth/captcha-config")
      );
      setCaptchaConfig(response.data);
    } catch (err) {
      console.error("Failed to fetch CAPTCHA config:", err);
    }
  };

  const handleCaptchaSuccess = async (verifyParam) => {
    setShowCaptcha(false);
    await sendPasswordResetEmail(email, verifyParam);
  };

  const handleCaptchaError = (error) => {
    setShowCaptcha(false);
    setMessage("验证失败，请重试或联系管理员");
  };

  // Get or generate device ID for tracking
  const getDeviceId = () => {
    let deviceId = localStorage.getItem("deviceId");
    if (!deviceId) {
      deviceId = `web-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem("deviceId", deviceId);
    }
    return deviceId;
  };

  const sendPasswordResetEmail = async (userEmail, captchaParam) => {
    setMessage("");
    setSuccessMessage("");

    try {
      const payload = {
        email: userEmail,
        podcast_id: podcastId,
        device_id: getDeviceId(), // Include device ID for tracking
      };
      if (captchaParam) {
        payload.captcha_verify_param = captchaParam;
      }

      const response = await axios.post(
        getFullApiUrl("/api/v1/auth/forgot"),
        payload
      );

      if (response.status === 200) {
        setSuccessMessage("重置密码邮件已发送，请检查您的邮箱");
        // Clear form after success
        setTimeout(() => {
          setEmail("");
          setSuccessMessage("");
        }, 5000);
      }
    } catch (err) {
      console.error("Forgot password error:", err);

      // Use translation helper
      const translated = translateAuthError(
        err.response?.data?.error || err.message,
        err
      );

      if (translated.captchaRequired || err.response?.data?.captcha_required) {
        // CAPTCHA required
        if (
          captchaConfig?.enabled &&
          captchaConfig?.app_key &&
          captchaConfig?.scene_id
        ) {
          setShowCaptcha(true);
        } else {
          // CAPTCHA not available
          setMessage("安全验证不可用，请联系管理员或稍后再试");
        }
      } else {
        setMessage(translated.message);
      }
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Validate email
    if (!validateEmail(email)) {
      setValidEmail(false);
      return;
    } else {
      setValidEmail(true);
    }

    // Always try to send password reset email first
    // CAPTCHA will be shown only if server requires it
    await sendPasswordResetEmail(email);
  };

  return (
    <AuthCard title="忘记密码" subtitle="输入邮箱，我们将向您发送密码重置链接。">
      <form onSubmit={handleSubmit} noValidate style={{ display: "grid", gap: 16 }}>
        <Field
          label="邮箱"
          htmlFor="email"
          error={!validEmail ? "电子邮件格式错误" : null}
        >
          <Input
            id="email"
            name="email"
            type="email"
            icon="mail"
            placeholder="you@example.com"
            autoComplete="email"
            autoFocus
            error={!validEmail}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (!validateEmail(e.target.value)) {
                setValidEmail(false);
              } else {
                setValidEmail(true);
              }
            }}
          />
        </Field>
        <Notice variant="error">{message}</Notice>
        {successMessage ? (
          <Notice variant="success">
            {successMessage}。如长时间未收到，请检查垃圾邮件。
          </Notice>
        ) : null}

        {showCaptcha && captchaConfig?.enabled && (
          <CaptchaVerification
            appKey={captchaConfig.app_key}
            sceneId={captchaConfig.scene_id}
            onSuccess={handleCaptchaSuccess}
            onError={handleCaptchaError}
            onCancel={() => {
              setShowCaptcha(false);
            }}
            embedded={true}
          />
        )}

        <Button
          type="submit"
          size="lg"
          fullWidth
          disabled={!validateEmail(email) || showCaptcha}
        >
          发送重置链接
        </Button>

        <div className="lt-auth-links">
          <Button type="button" variant="ghost" size="sm" onClick={() => navigate("/login")}>
            返回登录
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => navigate("/signup")}>
            注册
          </Button>
        </div>
      </form>
    </AuthCard>
  );
}
